/**
 * Client Map Tool
 * Interactive map showing LensLock client locations
 * Migrated from standalone HTML tool to React component
 */
import React, { useState, useEffect, useCallback, useMemo, useRef, Component, ErrorInfo, ReactNode } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle, CircleMarker, useMap } from "react-leaflet";
import type { Map as LeafletMap } from "leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Error boundary to catch map errors
interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class MapErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Map error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 20, textAlign: "center" }}>
          <h3>Map Error</h3>
          <p>Something went wrong loading the map. Please refresh the page.</p>
          <p style={{ color: "#666", fontSize: 12 }}>{this.state.error?.message}</p>
          <button onClick={() => window.location.reload()}>Refresh</button>
        </div>
      );
    }
    return this.props.children;
  }
}
import {
  makeStyles,
  tokens,
  Text,
  Card,
  CardHeader,
  Button,
  Input,
  Spinner,
} from "@fluentui/react-components";
import {
  Search24Regular,
  Location24Regular,
  ArrowDownload24Regular,
} from "@fluentui/react-icons";

// Fix for default marker icon in React-Leaflet
// Use CDN URLs for default marker icons
const defaultIconUrls = {
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
};

// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions(defaultIconUrls);

// Custom LensLock marker icon (full color for in-radius)
const lensLockIcon = new L.Icon({
  iconUrl: "/lenslock_mark_only_logo_color.png",
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16],
});

// Dimmed marker icon for clients outside search radius
const lensLockIconDimmed = new L.Icon({
  iconUrl: "/lenslock_mark_only_logo_color.png",
  iconSize: [22, 22],
  iconAnchor: [11, 11],
  popupAnchor: [0, -11],
  className: "dimmed-marker",
});

const useStyles = makeStyles({
  container: {
    display: "flex",
    height: "100%",
    gap: tokens.spacingHorizontalM,
  },
  mapSection: {
    flex: 2,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    minWidth: 0,
  },
  mapContainer: {
    flex: 1,
    borderRadius: "8px",
    overflow: "hidden",
    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.08)",
  },
  sidebar: {
    width: "350px",
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    padding: tokens.spacingVerticalM,
    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.08)",
    overflowY: "auto",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    paddingBottom: tokens.spacingVerticalS,
    borderBottom: "1px solid #e2e8f0",
    marginBottom: tokens.spacingVerticalS,
  },
  headerLogo: {
    height: "32px",
    width: "32px",
  },
  section: {
    marginBottom: tokens.spacingVerticalM,
  },
  sectionTitle: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#63666A",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: tokens.spacingVerticalS,
    fontFamily: "'Poppins', sans-serif",
  },
  searchInput: {
    width: "100%",
    marginBottom: tokens.spacingVerticalS,
  },
  searchRow: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalS,
  },
  radiusInput: {
    width: "100px",
  },
  searchButton: {
    flex: 1,
  },
  legend: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    fontSize: "13px",
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
  },
  legendIcon: {
    width: "20px",
    height: "20px",
    borderRadius: "50%",
  },
  results: {
    flex: 1,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
  stateGroup: {
    marginBottom: tokens.spacingVerticalS,
  },
  stateHeader: {
    fontWeight: 600,
    padding: "8px",
    backgroundColor: "#f1f5f9",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "13px",
    fontFamily: "'Poppins', sans-serif",
  },
  clientItem: {
    padding: "8px 8px 8px 16px",
    borderBottom: "1px solid #f1f5f9",
    cursor: "pointer",
    fontSize: "13px",
    transition: "background-color 0.15s",
    ":hover": {
      backgroundColor: "#f8fafc",
    },
  },
  distance: {
    fontSize: "11px",
    color: "#63666A",
    marginTop: "2px",
  },
  statsBar: {
    display: "flex",
    justifyContent: "space-around",
    padding: tokens.spacingVerticalS,
    backgroundColor: "#f8fafc",
    borderRadius: "6px",
    marginBottom: tokens.spacingVerticalS,
  },
  stat: {
    textAlign: "center",
  },
  statValue: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#004C97",
    fontFamily: "'Poppins', sans-serif",
  },
  statLabel: {
    fontSize: "11px",
    color: "#63666A",
  },
  exportButton: {
    marginTop: "auto",
  },
  error: {
    color: "#e53e3e",
    fontSize: "12px",
    marginTop: tokens.spacingVerticalXS,
  },
  loading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  },
});

// Client data type
interface Client {
  name: string;
  state: string;
  lat: number;
  lng: number;
  type: string;
  distance?: number;
}

// Haversine distance calculation (miles)
function haversineMiles(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3958.8; // Earth radius in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Component to capture map reference and handle view updates
function MapController({ 
  mapRef,
  clients, 
  searchCenter, 
  zoomToClient 
}: { 
  mapRef: React.MutableRefObject<LeafletMap | null>;
  clients: Client[]; 
  searchCenter: [number, number] | null;
  zoomToClient: { client: Client; timestamp: number } | null;
}) {
  const map = useMap();
  
  // Store map reference immediately
  useEffect(() => {
    mapRef.current = map;
  }, [map, mapRef]);

  // Zoom to specific client when clicked
  useEffect(() => {
    try {
      if (zoomToClient?.client?.lat && zoomToClient?.client?.lng) {
        const { lat, lng } = zoomToClient.client;
        if (typeof lat === 'number' && typeof lng === 'number') {
          map.setView([lat, lng], 15);
        }
      }
    } catch (err) {
      console.error("Error zooming to client:", err);
    }
  }, [zoomToClient, map]);

  // Fit bounds to all clients on initial load (only when no search)
  useEffect(() => {
    try {
      if (searchCenter) {
        return; // Don't auto-fit when there's a search
      }
      if (clients.length > 0) {
        const validClients = clients.filter((c) => c.lat && c.lng);
        if (validClients.length > 0) {
          const bounds = L.latLngBounds(validClients.map((c) => [c.lat, c.lng]));
          map.fitBounds(bounds, { padding: [50, 50] });
        }
      }
    } catch (err) {
      console.error("Error fitting to client bounds:", err);
    }
  }, [clients, map, searchCenter]);

  return null;
}

export const ClientMap: React.FC = () => {
  const styles = useStyles();
  const [clients, setClients] = useState<Client[]>([]);
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Map reference for direct control
  const mapRef = useRef<LeafletMap | null>(null);

  // Search state
  const [locationSearch, setLocationSearch] = useState("");
  const [radiusMiles, setRadiusMiles] = useState(50);
  const [agencySearch, setAgencySearch] = useState("");
  const [searchCenter, setSearchCenter] = useState<[number, number] | null>(null);
  const [searchLocationName, setSearchLocationName] = useState<string | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  
  // Set of client indices that are within the current search radius
  const [clientsInRadius, setClientsInRadius] = useState<Set<number>>(new Set());

  // Collapsible state groups
  const [expandedStates, setExpandedStates] = useState<Set<string>>(new Set());

  // Zoom to client - use a tuple with timestamp to allow clicking same client twice
  const [zoomToClient, setZoomToClient] = useState<{ client: Client; timestamp: number } | null>(null);


  // Load client data
  useEffect(() => {
    const loadClients = async () => {
      try {
        const response = await fetch("/data/clients.json");
        if (!response.ok) throw new Error("Failed to load client data");
        const data = await response.json();
        setClients(data);
        setFilteredClients(data);
      } catch (err) {
        setError("Failed to load client data");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    loadClients();
  }, []);

  // Filter by agency name
  useEffect(() => {
    if (!agencySearch.trim()) {
      setFilteredClients(clients);
      return;
    }
    const term = agencySearch.toLowerCase();
    const filtered = clients.filter(
      (c) =>
        c.name.toLowerCase().includes(term) ||
        c.state.toLowerCase().includes(term)
    );
    setFilteredClients(filtered);
    setSearchCenter(null);
    setSearchLocationName(null);
  }, [agencySearch, clients]);

  // Process search results after geocoding
  const processSearchResults = useCallback((lat: number, lng: number, displayName: string) => {
    // Calculate distances for all clients
    const withDistances = clients
      .filter((c) => c.lat && c.lng)
      .map((c, idx) => ({
        ...c,
        originalIndex: idx,
        distance: parseFloat(haversineMiles(lat, lng, c.lat, c.lng).toFixed(1)),
      }));
    
    // Find clients within radius
    const inRadius = withDistances
      .filter((c) => c.distance <= radiusMiles)
      .sort((a, b) => a.distance - b.distance);
    
    // Track which original indices are in radius
    const inRadiusSet = new Set(inRadius.map(c => c.originalIndex));
    setClientsInRadius(inRadiusSet);

    setFilteredClients(inRadius);
    setSearchCenter([lat, lng]);
    setSearchLocationName(displayName);
    setAgencySearch("");
    
    // Zoom map directly - calculate bounds manually (circle.getBounds() requires being on a map)
    setTimeout(() => {
      if (mapRef.current) {
        // Calculate bounds manually: 1 degree latitude ≈ 69 miles, longitude varies by latitude
        const latOffset = radiusMiles / 69;
        const lngOffset = radiusMiles / (69 * Math.cos(lat * Math.PI / 180));
        const bounds = L.latLngBounds(
          [lat - latOffset, lng - lngOffset],
          [lat + latOffset, lng + lngOffset]
        );
        mapRef.current.fitBounds(bounds, { padding: [20, 20] });
      }
    }, 100);
  }, [clients, radiusMiles]);

  // Geocode and search by location
  const handleLocationSearch = useCallback(async () => {
    if (!locationSearch.trim()) {
      setSearchError("Please enter a location to search.");
      return;
    }

    setIsSearching(true);
    setSearchError(null);

    try {
      const zipRegex = /^\d{5}$/;

      if (zipRegex.test(locationSearch)) {
        // Use Zippopotam.us for accurate US ZIP code lookup
        const response = await fetch(`https://api.zippopotam.us/us/${locationSearch}`);
        if (!response.ok) {
          setSearchError("ZIP code not found. Please check and try again.");
          setIsSearching(false);
          return;
        }
        const data = await response.json();
        const place = data.places[0];
        const lat = parseFloat(place.latitude);
        const lng = parseFloat(place.longitude);
        const displayName = `${place["place name"]}, ${place["state abbreviation"]} ${locationSearch}`;
        processSearchResults(lat, lng, displayName);
      } else {
        // Use Photon API for city/state/address searches
        const response = await fetch(
          `https://photon.komoot.io/api/?q=${encodeURIComponent(locationSearch)}&limit=5&lang=en`
        );
        const data = await response.json();

        if (data?.features?.length > 0) {
          // Filter to US results
          const usResults = data.features.filter(
            (f: any) => f.properties?.country === "United States"
          );

          if (usResults.length > 0) {
            const result = usResults[0];
            const [lng, lat] = result.geometry.coordinates;
            const props = result.properties;
            const displayName = [props.name, props.city, props.state, props.country]
              .filter(Boolean)
              .join(", ");
            processSearchResults(lat, lng, displayName);
          } else {
            setSearchError("Location found outside the United States. Please try a more specific search.");
          }
        } else {
          setSearchError("Location not found. Please try a different search.");
        }
      }
    } catch (err) {
      setSearchError("Error searching location. Please try again.");
      console.error(err);
    } finally {
      setIsSearching(false);
    }
  }, [locationSearch, processSearchResults]);

  // Group results by state
  const groupedByState = useMemo(() => {
    const groups: Record<string, Client[]> = {};
    filteredClients.forEach((c) => {
      const state = c.state || "Unknown";
      if (!groups[state]) groups[state] = [];
      groups[state].push(c);
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [filteredClients]);

  // Toggle state group
  const toggleState = (state: string) => {
    setExpandedStates((prev) => {
      const next = new Set(prev);
      if (next.has(state)) next.delete(state);
      else next.add(state);
      return next;
    });
  };

  // Export to CSV
  const handleExport = () => {
    if (filteredClients.length === 0) {
      alert("No results to export.");
      return;
    }

    const csvEscape = (val: any) => {
      const v = val === null || val === undefined ? "" : String(val);
      return '"' + v.replace(/"/g, '""') + '"';
    };

    let csv = '"Agency Name","State","Latitude","Longitude","Distance (miles)"\r\n';
    filteredClients.forEach((c) => {
      csv += [
        csvEscape(c.name),
        csvEscape(c.state),
        csvEscape(c.lat),
        csvEscape(c.lng),
        csvEscape(c.distance !== undefined ? c.distance : ""),
      ].join(",") + "\r\n";
    });

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "lenslock_client_search_results.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Clear search
  const handleClearSearch = () => {
    setLocationSearch("");
    setAgencySearch("");
    setSearchCenter(null);
    setSearchLocationName(null);
    setSearchError(null);
    setFilteredClients(clients);
    setClientsInRadius(new Set());
  };

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner size="large" label="Loading client data..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.loading}>
        <Text style={{ color: "#e53e3e" }}>{error}</Text>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Map Section */}
      <div className={styles.mapSection}>
        <div className={styles.mapContainer}>
          <MapErrorBoundary>
            <MapContainer
              center={[39.8283, -98.5795]}
              zoom={4}
              style={{ height: "100%", width: "100%" }}
            >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapController 
              mapRef={mapRef}
              clients={clients} 
              searchCenter={searchCenter} 
              zoomToClient={zoomToClient}
            />

            {/* All client markers - show all clients always */}
            {clients.map((client, idx) => {
              if (!client.lat || !client.lng) return null;
              const isInRadius = searchCenter ? clientsInRadius.has(idx) : true;
              const matchedClient = isInRadius 
                ? filteredClients.find(c => c.name === client.name && c.state === client.state)
                : null;
              
              return (
                <Marker
                  key={`${client.name}-${idx}`}
                  position={[client.lat, client.lng]}
                  icon={isInRadius ? lensLockIcon : lensLockIconDimmed}
                  opacity={isInRadius ? 1 : 0.95}
                  zIndexOffset={isInRadius ? 1000 : 0}
                >
                  <Popup>
                    <div style={{ padding: "4px" }}>
                      <strong>{client.name}</strong>
                      <br />
                      <span style={{ color: "#666" }}>{client.state}</span>
                      {matchedClient?.distance !== undefined && (
                        <>
                          <br />
                          <span style={{ color: "#004C97" }}>{matchedClient.distance} miles away</span>
                        </>
                      )}
                      {searchCenter && !isInRadius && (
                        <>
                          <br />
                          <span style={{ color: "#999", fontSize: "11px" }}>Outside search radius</span>
                        </>
                      )}
                    </div>
                  </Popup>
                </Marker>
              );
            })}

            {/* Search center marker */}
            {searchCenter && (
              <>
                <CircleMarker
                  center={searchCenter}
                  radius={10}
                  pathOptions={{ color: "red", fillColor: "red", fillOpacity: 0.9 }}
                />
                <Circle
                  center={searchCenter}
                  radius={radiusMiles * 1609.34}
                  pathOptions={{ color: "blue", fillColor: "blue", fillOpacity: 0.05, weight: 2 }}
                />
              </>
            )}
            </MapContainer>
          </MapErrorBoundary>
        </div>
      </div>

      {/* Sidebar */}
      <div className={styles.sidebar}>
        {/* Header */}
        <div className={styles.header}>
          <img
            src="/lenslock_mark_only_logo_color.png"
            alt="LensLock"
            className={styles.headerLogo}
          />
          <Text size={500} weight="semibold" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Client Search Tool
          </Text>
        </div>


        {/* Legend */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Map Legend</div>
          <div className={styles.legend}>
            <div className={styles.legendItem}>
              <img src="/lenslock_mark_only_logo_color.png" alt="" style={{ width: 20, height: 20 }} />
              <span>{searchCenter ? "Client (in radius)" : "Client Location"}</span>
            </div>
            {searchCenter && (
              <div className={styles.legendItem}>
                <img src="/lenslock_mark_only_logo_color.png" alt="" style={{ width: 16, height: 16, opacity: 0.95 }} />
                <span>Client (outside radius)</span>
              </div>
            )}
            <div className={styles.legendItem}>
              <div className={styles.legendIcon} style={{ backgroundColor: "red" }} />
              <span>Search Location</span>
            </div>
            <div className={styles.legendItem}>
              <div style={{ width: 20, height: 3, backgroundColor: "blue", borderRadius: 2 }} />
              <span>Search Radius</span>
            </div>
          </div>
        </div>

        {/* Location Search */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Location Search</div>
          <Input
            className={styles.searchInput}
            placeholder="City, State, or ZIP"
            value={locationSearch}
            onChange={(e, data) => setLocationSearch(data.value)}
            onKeyDown={(e) => e.key === "Enter" && handleLocationSearch()}
          />
          <div className={styles.searchRow}>
            <Input
              className={styles.radiusInput}
              type="number"
              min={0}
              value={radiusMiles.toString()}
              onChange={(e, data) => setRadiusMiles(parseInt(data.value) || 50)}
              contentAfter={<Text size={200}>mi</Text>}
            />
            <Button
              className={styles.searchButton}
              appearance="primary"
              icon={<Search24Regular />}
              onClick={handleLocationSearch}
              disabled={isSearching}
            >
              {isSearching ? "Searching..." : "Search"}
            </Button>
          </div>
          {searchError && <div className={styles.error}>{searchError}</div>}
          {searchLocationName && (
            <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
              Showing {filteredClients.length} clients within {radiusMiles} mi of{" "}
              <em>{searchLocationName}</em>
              <Button
                appearance="transparent"
                size="small"
                onClick={handleClearSearch}
                style={{ marginLeft: 8, minWidth: 0, padding: "0 4px" }}
              >
                Clear
              </Button>
            </div>
          )}
        </div>

        {/* Agency Search */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Agency Search</div>
          <Input
            className={styles.searchInput}
            placeholder="Search agencies..."
            contentBefore={<Search24Regular />}
            value={agencySearch}
            onChange={(e, data) => setAgencySearch(data.value)}
          />
        </div>

        {/* Results */}
        <div className={styles.section} style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          <div className={styles.sectionTitle}>
            Results ({filteredClients.length})
          </div>
          <div className={styles.results}>
            {groupedByState.map(([state, stateClients]) => (
              <div key={state} className={styles.stateGroup}>
                <div
                  className={styles.stateHeader}
                  onClick={() => toggleState(state)}
                >
                  {state} ({stateClients.length}) {expandedStates.has(state) ? "▼" : "▶"}
                </div>
                {expandedStates.has(state) &&
                  stateClients.map((client, idx) => (
                    <div
                      key={`${client.name}-${idx}`}
                      className={styles.clientItem}
                      onClick={() => setZoomToClient({ client, timestamp: Date.now() })}
                      title="Click to zoom to location"
                    >
                      {client.name}
                      {client.distance !== undefined && (
                        <div className={styles.distance}>{client.distance} miles away</div>
                      )}
                    </div>
                  ))}
              </div>
            ))}
          </div>
        </div>

        {/* Export Button */}
        <Button
          className={styles.exportButton}
          appearance="secondary"
          icon={<ArrowDownload24Regular />}
          onClick={handleExport}
        >
          Export Results
        </Button>
      </div>
    </div>
  );
};

export default ClientMap;
